import { ApolloError, useQuery } from '@apollo/client'
import { gql } from '__generated__'
import { GetSkiptracePlansInfoQuery, SubscriptionFeatureSkiptrace } from '__generated__/graphql'

// GraphQL query to get all subscription products and the user's current plan
const GET_SKIPTRACE_PLANS_INFO = gql(/* GraphQL */`
  query GetSkiptracePlansInfo {
    billingSubscriptionProducts {
      id
      name
      features {
        ... on SubscriptionFeatureSkiptrace {
          limits {
            interval
            value
          }
        }
      }
    }
    
    myEnterprise {
      id
      subscription {
        ... on BillingSubscriptionActive {
          id
          plan {
            id
            product {
              id
              features {
                ... on SubscriptionFeatureSkiptrace {
                  limits {
                    interval
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`)

/**
 * Hook to determine if there are plans with more skiptrace than the user's current plan.
 * Returns true if such plans exist, which means the user could benefit from upgrading.
 */
export const useHasMoreSkiptracePlans = (enabled = true): {
  hasMoreSkiptracePlans: boolean
  currentSkiptraceAllowance: number
  maxSkiptraceAvailable: number
  loading: boolean
  error: ApolloError | null
} => {
  const { data, loading, error } = useQuery(GET_SKIPTRACE_PLANS_INFO, {
    skip: !enabled,
  })

  if (!enabled)
    return { hasMoreSkiptracePlans: false, currentSkiptraceAllowance: 0, maxSkiptraceAvailable: 0, loading: false, error: null }

  if (loading || error || !data)
    return { hasMoreSkiptracePlans: false, currentSkiptraceAllowance: 0, maxSkiptraceAvailable: 0, loading, error: error ?? null }

  // Get the current subscription
  const subscription = data.myEnterprise?.subscription

  // If there's no subscription or it's not active, consider all plans as having more skiptrace
  if (!subscription || subscription.__typename !== 'BillingSubscriptionActive')
    return { hasMoreSkiptracePlans: true, currentSkiptraceAllowance: 0, maxSkiptraceAvailable: getMaxSkiptraceValue(data), loading: false, error: null }

  // Get the current plan's skiptrace limit
  const currentPlanFeatures = subscription.plan?.product?.features || []
  const currentSkiptraceAllowance = getSkiptraceValue(currentPlanFeatures)

  // Find the maximum skiptrace available across all plans
  const maxSkiptraceAvailable = getMaxSkiptraceValue(data)

  // Determine if there are plans with more skiptrace
  // If current plan already has unlimited skiptrace (Infinity), no plan can have more
  // Otherwise, check if maxskiptraceAvailable is greater than current allowance
  const hasMoreSkiptracePlans = currentSkiptraceAllowance === Infinity ? false : maxSkiptraceAvailable > currentSkiptraceAllowance

  return {
    hasMoreSkiptracePlans,
    currentSkiptraceAllowance,
    maxSkiptraceAvailable,
    loading: false,
    error: null,
  }
}

/**
 * Helper function to extract the skiptrace value from feature array
 */
const getSkiptraceValue = (features: GetSkiptracePlansInfoQuery['billingSubscriptionProducts'][number]['features']): number => {
  // Find the SubscriptionFeatureskiptrace feature
  const skiptraceFeature = features.find(f => f.__typename === 'SubscriptionFeatureSkiptrace') as SubscriptionFeatureSkiptrace | undefined

  if (!skiptraceFeature)
    return 0

  // If skiptrace feature exists but has no limits or empty limits array,
  // it means unlimited skiptrace (represented as Infinity)
  if (!skiptraceFeature.limits || skiptraceFeature.limits.length === 0)
    return Infinity

  // Get the highest value limit
  return Math.max(...skiptraceFeature.limits.map(limit => limit.value || 0))
}

/**
 * Helper function to find the maximum skiptrace value across all plans
 */
const getMaxSkiptraceValue = (data: GetSkiptracePlansInfoQuery): number => {
  if (!data.billingSubscriptionProducts)
    return 0

  // Map each product to its skiptrace limit
  const skiptraceLimits = data.billingSubscriptionProducts.map((product: any) => getSkiptraceValue(product.features))

  // Return the highest skiptrace limit
  return Math.max(0, ...skiptraceLimits)
}
